/**
 * Builds List - Display EAS builds in a table
 */

import { ReactTable } from "@aix/ui"

interface BuildsListProps {
  builds: any[]
  isLoading: boolean
}

export function BuildsList({ builds, isLoading }: BuildsListProps) {
  if (isLoading) {
    return <div className="text-muted-foreground">Loading builds...</div>
  }

  if (builds.length === 0) {
    return <div className="text-muted-foreground">No builds found</div>
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "finished":
        return "bg-green-100 text-green-800"
      case "in-progress":
      case "pending":
        return "bg-blue-100 text-blue-800"
      case "errored":
      case "failed":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const columns = [
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }: any) => (
        <span className={`px-2 py-1 text-xs rounded ${getStatusColor(row.status)}`}>{row.status}</span>
      ),
    },
    {
      Header: "Platform",
      accessor: "platform",
      Cell: ({ row }: any) => (
        <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded">
          {row.platform === "ios" ? "📱 iOS" : "🤖 Android"}
        </span>
      ),
    },
    {
      Header: "Version",
      accessor: "appVersion",
      Cell: ({ row }: any) => (
        <div className="text-sm">
          {row.appVersion && <p className="text-foreground">{row.appVersion}</p>}
          {row.appBuildVersion && <p className="text-xs text-muted-foreground">Build: {row.appBuildVersion}</p>}
        </div>
      ),
    },
    {
      Header: "Runtime Version",
      accessor: "runtimeVersion",
      Cell: ({ row }: any) => (
        <span className="text-xs font-mono text-muted-foreground">
          {row.runtimeVersion ? `${row.runtimeVersion.substring(0, 12)}...` : "—"}
        </span>
      ),
    },
    {
      Header: "Channel",
      accessor: "channel",
      Cell: ({ row }: any) => (
        <span className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded">{row.channel || "—"}</span>
      ),
    },
    {
      Header: "Created",
      accessor: "createdAt",
      Cell: ({ row }: any) => {
        const date = new Date(row.createdAt)
        return (
          <div className="text-sm">
            <p className="text-foreground">{date.toLocaleDateString()}</p>
            <p className="text-xs text-muted-foreground">{date.toLocaleTimeString()}</p>
          </div>
        )
      },
    },
    {
      Header: "Actions",
      accessor: "id",
      Cell: ({ row }: any) => (
        <div className="flex gap-2">
          {row.artifacts?.buildUrl && (
            <a
              href={row.artifacts.buildUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline"
            >
              Download
            </a>
          )}
          <a
            href={`https://expo.dev/accounts/[account]/projects/[project]/builds/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
          >
            EAS
          </a>
        </div>
      ),
    },
  ]

  return (
    <div>
      <ReactTable data={builds} columns={columns} />
    </div>
  )
}
